import { useCallback, useEffect, useState } from 'react';

export type ProbeStatus = 'loading' | 'ok' | 'degraded' | 'error';

export type DevHealthSnapshot = {
  apiLive: ProbeStatus;
  dbReady: ProbeStatus;
  lastChecked: Date | null;
  detail: string | null;
};

/** Dev-only health poll interval — keep long enough not to interfere with compose UAT. */
export const DEV_HEALTH_REFRESH_MS = 60_000;

async function probeHealth(apiPrefix: string): Promise<Pick<DevHealthSnapshot, 'apiLive' | 'dbReady' | 'detail'>> {
  try {
    const liveResponse = await fetch(`${apiPrefix}/healthz`);
    if (!liveResponse.ok) {
      return {
        apiLive: 'error',
        dbReady: 'error',
        detail: `API liveness returned HTTP ${liveResponse.status}`,
      };
    }

    const readyResponse = await fetch(`${apiPrefix}/readyz`);
    if (readyResponse.ok) {
      return { apiLive: 'ok', dbReady: 'ok', detail: null };
    }

    if (readyResponse.status === 503) {
      return {
        apiLive: 'ok',
        dbReady: 'degraded',
        detail: 'API is up but PostgreSQL readiness check failed',
      };
    }

    return {
      apiLive: 'ok',
      dbReady: 'error',
      detail: `Readiness returned HTTP ${readyResponse.status}`,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Network error';
    return {
      apiLive: 'error',
      dbReady: 'error',
      detail: message,
    };
  }
}

/** Poll API health probes for the development scaffold dashboard. */
export function useDevHealth(enabled: boolean): DevHealthSnapshot & { refresh: () => void } {
  const [snapshot, setSnapshot] = useState<DevHealthSnapshot>({
    apiLive: 'loading',
    dbReady: 'loading',
    lastChecked: null,
    detail: null,
  });

  const apiPrefix = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_BASE_URL ?? '');

  const refresh = useCallback(
    (showLoading = true) => {
      if (!enabled || !apiPrefix) {
        setSnapshot({
          apiLive: 'error',
          dbReady: 'error',
          lastChecked: new Date(),
          detail: 'API base URL is not configured',
        });
        return;
      }

      if (showLoading) {
        setSnapshot((current) => ({
          ...current,
          apiLive: 'loading',
          dbReady: 'loading',
          detail: null,
        }));
      }

      void probeHealth(apiPrefix).then((result) => {
        setSnapshot({
          ...result,
          lastChecked: new Date(),
        });
      });
    },
    [apiPrefix, enabled],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    refresh(true);
    const timer = window.setInterval(() => refresh(false), DEV_HEALTH_REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [enabled, refresh]);

  return { ...snapshot, refresh };
}
